import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faPaperPlane,
  faReply,
  faTimesCircle,
  faExclamationTriangle,
  faCalendarAlt,
  faSyncAlt,
  faFilter,
  faSearch,
  faInfoCircle,
  faUser,
  faFileInvoice,
  faFileExcel,
  faTimes,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import { useStatisticsService } from '../services/statistics.service';
import { useAuth } from '../services/auth.service';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ApexCharts from 'apexcharts';
import XLSX from 'xlsx';
import axios from 'axios';

// Styled Components for Modern Premium Look
const MetricCard = styled(Card)(({ gradient }) => ({
  background: gradient || 'linear-gradient(135deg, #008069 0%, #00a884 100%)',
  color: 'white',
  borderRadius: '16px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: 'none',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.16)',
  },
}));

const FilterContainer = styled(Box)({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '24px',
  border: '1px solid #e9edef',
  marginBottom: '24px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)',
});

const StyledTableContainer = styled(TableContainer)({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
  overflow: 'hidden',
  border: '1px solid #e9edef',
  backgroundColor: '#ffffff',
});

const StyledTableCell = styled(TableCell)({
  fontWeight: 600,
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef',
  color: '#111b21',
  padding: '14px 16px',
});

const DetailPre = styled('pre')({
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  fontSize: '11px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9edef',
  borderRadius: '8px',
  padding: '12px',
  maxHeight: '300px',
  overflowY: 'auto',
  margin: 0,
  fontFamily: 'SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace',
});

export default function ReporteAutomatizaciones() {
  const { getAutomationReport, getAutomations, getAutomationAudits } = useStatisticsService();
  const { user } = useAuth();

  // Active Tab State
  const [activeTab, setActiveTab] = useState('executions'); // 'executions' or 'audits'

  // Filters State
  const [dateType, setDateType] = useState('range'); // 'range', 'month', 'day', 'all'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [automationId, setAutomationId] = useState('');
  const [ejecutivo, setEjecutivo] = useState('');
  const [templateName, setTemplateName] = useState('');
  
  // Automations list state
  const [automations, setAutomations] = useState([]);
  const [automationsLoading, setAutomationsLoading] = useState(false);

  // Pagination State (Executions)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Pagination State (Audits)
  const [auditPage, setAuditPage] = useState(1);
  const [auditLimit, setAuditLimit] = useState(20);

  // Data States (Executions)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  // Data States (Audits)
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);

  // Verification Status States
  const [verifyingWaid, setVerifyingWaid] = useState({});
  const [waidErrors, setWaidErrors] = useState({});
  const [verifiedStatuses, setVerifiedStatuses] = useState({});

  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Fetch automations list on mount
  useEffect(() => {
    const loadAutomationsList = async () => {
      if (!user) return;
      setAutomationsLoading(true);
      try {
        const data = await getAutomations();
        setAutomations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading automations list:', err);
      } finally {
        setAutomationsLoading(false);
      }
    };
    loadAutomationsList();
  }, [user]);

  // Set default dates on mount (current month range)
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    
    // Default to current month: from 1st of month to today
    setStartDate(`${year}-${m}-01`);
    
    const dayStr = String(now.getDate()).padStart(2, '0');
    setEndDate(`${year}-${m}-${dayStr}`);
  }, []);

  // Fetch Report Data
  const loadReport = async (targetPage = page) => {
    setLoading(true);
    setError(null);

    const filters = {
      page: targetPage,
      limit
    };

    // Apply date filters based on chosen dateType
    if (dateType === 'range') {
      if (startDate) filters.from = new Date(startDate + 'T00:00:00').toISOString();
      if (endDate) filters.to = new Date(endDate + 'T23:59:59').toISOString();
    } else if (dateType === 'month') {
      if (month) filters.month = month;
    } else if (dateType === 'day') {
      if (day) filters.day = day;
    }

    if (automationId.trim()) filters.automationId = automationId.trim();
    if (ejecutivo.trim()) filters.ejecutivo = ejecutivo.trim();
    if (templateName.trim()) filters.templateName = templateName.trim();

    try {
      const data = await getAutomationReport(filters);
      setReportData(data);
      // If server returned normalized filters, we update page if needed
      if (data?.pagination) {
        setPage(data.pagination.page || targetPage);
      }
    } catch (err) {
      console.error('Error loading automation report:', err);
      setError('No se pudo cargar el reporte de automatizaciones. Por favor, verifique su conexión o intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Audits Data
  const loadAudits = async (targetPage = auditPage) => {
    setAuditLoading(true);
    setAuditError(null);

    const filters = {
      page: targetPage,
      limit: auditLimit
    };

    // Apply date filters based on chosen dateType
    if (dateType === 'range') {
      if (startDate) filters.from = new Date(startDate + 'T00:00:00').toISOString();
      if (endDate) filters.to = new Date(endDate + 'T23:59:59').toISOString();
    } else if (dateType === 'month') {
      if (month) filters.month = month;
    } else if (dateType === 'day') {
      if (day) filters.day = day;
    }

    if (automationId) filters.automationId = automationId;
    if (ejecutivo.trim()) filters.ejecutivo = ejecutivo.trim();
    if (templateName.trim()) filters.templateName = templateName.trim();

    try {
      const data = await getAutomationAudits(filters);
      setAuditData(data);
      if (data?.page) {
        setAuditPage(data.page);
      }
    } catch (err) {
      console.error('Error loading audits:', err);
      setAuditError('No se pudo cargar la auditoría de envíos.');
    } finally {
      setAuditLoading(false);
    }
  };

  // Verify wamid message status
  const handleVerifyMessageStatus = async (waid) => {
    if (!waid) return;
    setVerifyingWaid(prev => ({ ...prev, [waid]: 'checking' }));
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API}/messages/wamid/${waid}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": user?.accessToken,
          }
        }
      );
      const apiRes = response.data;
      const messageData = apiRes && apiRes.success ? apiRes.data : apiRes;

      const hasError = !messageData || 
                       messageData.error || 
                       messageData.status === 'failed' || 
                       messageData.message_status === 'failed' || 
                       messageData.errorCode || 
                       messageData.errorMessage;

      if (hasError) {
        let errorMsg = "Mensaje con error";
        if (messageData?.error) {
          errorMsg = messageData.error.details || messageData.error.message || messageData.error.title || errorMsg;
        } else if (messageData?.errorMessage) {
          errorMsg = messageData.errorMessage;
        }
        setVerifyingWaid(prev => ({ ...prev, [waid]: 'error' }));
        setWaidErrors(prev => ({ ...prev, [waid]: errorMsg }));
      } else {
        const newStatus = messageData.status || messageData.message_status || 'delivered';
        setVerifyingWaid(prev => ({ ...prev, [waid]: 'verified' }));
        setVerifiedStatuses(prev => ({ ...prev, [waid]: newStatus }));
      }
    } catch (err) {
      console.error("Error verifying message status", err);
      setVerifyingWaid(prev => ({ ...prev, [waid]: 'error' }));
      const errorMsg = err.response?.data?.message || err.message || "Error de red/servidor";
      setWaidErrors(prev => ({ ...prev, [waid]: errorMsg }));
    }
  };

  // Reset verification caches on tab change or report data reload
  useEffect(() => {
    setVerifyingWaid({});
    setWaidErrors({});
    setVerifiedStatuses({});
  }, [activeTab, reportData]);

  // Trigger load when page, limit or tab selection changes
  useEffect(() => {
    if (startDate || month || day || dateType === 'all') {
      if (activeTab === 'executions') {
        loadReport(page);
      } else {
        loadAudits(auditPage);
      }
    }
  }, [page, limit, auditPage, auditLimit, activeTab]);

  // Handle Search / Filter Click
  const handleFilterSubmit = (e) => {
    if (e) e.preventDefault();
    setPage(1);
    setAuditPage(1);
    if (activeTab === 'executions') {
      loadReport(1);
    } else {
      loadAudits(1);
    }
  };

  // Clear Filters
  const handleClearFilters = () => {
    setDateType('range');
    const now = new Date();
    const year = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const dayStr = String(now.getDate()).padStart(2, '0');
    
    setStartDate(`${year}-${m}-01`);
    setEndDate(`${year}-${m}-${dayStr}`);
    setMonth('');
    setDay('');
    setAutomationId('');
    setEjecutivo('');
    setTemplateName('');
    setPage(1);
    setAuditPage(1);
    setAuditData(null);
    setAuditError(null);
  };

  // ApexCharts rendering logic
  useEffect(() => {
    if (!reportData || loading) return;

    let statusChart;
    let execPerformanceChart;

    // 1. Status Breakdown Donut Chart
    const statusEl = document.getElementById("status-breakdown-chart");
    if (statusEl) {
      const sum = reportData.summary || {};
      const sent = sum.totalSent || 0;
      const failed = sum.totalFailed || 0;
      const skipped = sum.totalSkipped || 0;

      const options = {
        chart: {
          type: 'donut',
          height: 320,
          fontFamily: 'Inter, sans-serif',
          id: 'status-breakdown-chart-id'
        },
        series: [sent, failed, skipped],
        labels: ['Enviados', 'Fallidos', 'Omitidos (Skipped)'],
        colors: ['#00a884', '#f43f5e', '#f59e0b'],
        legend: {
          position: 'bottom',
          fontSize: '13px',
          fontWeight: 500,
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            return opts.w.config.series[opts.seriesIndex];
          }
        },
        tooltip: {
          y: {
            formatter: (val) => `${val} ejecuciones`
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total Intentos',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111b21',
                  formatter: () => sum.totalAttempted || 0
                }
              }
            }
          }
        }
      };
      statusChart = new ApexCharts(statusEl, options);
      statusChart.render();
    }

    // 2. Executive Performance Chart
    const execEl = document.getElementById("executives-perf-chart");
    const execs = reportData.ejecutivosSummary || [];
    if (execEl && execs.length > 0) {
      // Sort and take top 8
      const topExecs = [...execs]
        .sort((a, b) => (b.sentCount || b.totalSent || 0) - (a.sentCount || a.totalSent || 0))
        .slice(0, 8);

      const options = {
        chart: {
          type: 'bar',
          height: 320,
          fontFamily: 'Inter, sans-serif',
          id: 'executives-perf-chart-id',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '50%',
            borderRadius: 6
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => val
        },
        series: [
          {
            name: 'Enviados',
            data: topExecs.map(e => e.sentCount || e.totalSent || 0)
          }
        ],
        xaxis: {
          categories: topExecs.map(e => e.ejecutivo || 'Desconocido'),
          labels: {
            style: {
              colors: '#667085',
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: '#667085',
              fontSize: '12px'
            }
          }
        },
        colors: ['#3b82f6', '#10b981', '#6366f1'],
        legend: {
          position: 'top',
          fontSize: '13px',
          fontWeight: 500,
        },
        tooltip: {
          y: {
            formatter: (val) => `${val} ejecuciones`
          }
        }
      };
      execPerformanceChart = new ApexCharts(execEl, options);
      execPerformanceChart.render();
    }

    // Cleanup charts
    return () => {
      if (statusChart) statusChart.destroy();
      if (execPerformanceChart) execPerformanceChart.destroy();
    };

  }, [reportData, loading]);

  // Export all loaded page items to Excel
  const handleExportExcel = () => {
    if (!reportData || !reportData.items || reportData.items.length === 0) {
      alert("No hay datos para exportar.");
      return;
    }

    const exportData = reportData.items.map(item => ({
      ID: item._id || '',
      Fecha: item.createdAt ? new Date(item.createdAt).toLocaleString() : '',
      'Automation ID': item.automationId || '',
      Template: item.templateName || '',
      Ejecutivo: item.ejecutivo || '',
      Celular: item.celular || item.recipient || '',
      Status: item.status || item.eventType || '',
      Detalle: item.errorMessage || item.detail || ''
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte Automatizaciones");
    
    // Generate filename
    const dateStamp = new Date().toISOString().slice(0, 10);
    const fileName = `Reporte_Automatizaciones_${dateStamp}.xlsx`;
    XLSX.writeFile(workbook, fileName, { compression: true });
  };

  const getStatusChipColor = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('sent') || s === 'enviado' || s === 'success') {
      return { bg: '#e6f4ea', text: '#137333', label: 'Enviado' };
    }
    if (s.includes('fail') || s === 'error' || s === 'failed') {
      return { bg: '#fce8e6', text: '#c5221f', label: 'Fallido' };
    }
    if (s.includes('skip') || s === 'omitido') {
      return { bg: '#fef7e0', text: '#b06000', label: 'Omitido' };
    }
    if (s.includes('reply') || s === 'respondido' || s === 'replied') {
      return { bg: '#e8f0fe', text: '#1a73e8', label: 'Respondido' };
    }
    return { bg: '#f1f3f4', text: '#3c4043', label: status || 'Intentado' };
  };

  const openDetails = (item) => {
    setSelectedItem(item);
    setDetailOpen(true);
  };

  const summary = reportData?.summary || {};
  const pagination = reportData?.pagination || {};

  return (
    <Box sx={{ p: 4, maxWidth: '1600px', margin: '0 auto', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Title Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#111b21', mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FontAwesomeIcon icon={faRobot} style={{ color: '#008069' }} />
            Reporte de Automatizaciones
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitoree la efectividad, respuestas, errores y omisiones de los procesos de envío automatizados.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => loadReport(page)}
            disabled={loading}
            startIcon={<FontAwesomeIcon icon={faSyncAlt} spin={loading} />}
            sx={{
              borderColor: '#e9edef',
              color: '#495057',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              backgroundColor: '#ffffff',
              '&:hover': {
                backgroundColor: '#f1f3f5',
                borderColor: '#d2d6dc',
              },
            }}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            onClick={handleExportExcel}
            disabled={loading || !reportData}
            startIcon={<FontAwesomeIcon icon={faFileExcel} />}
            sx={{
              backgroundColor: '#198754',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: '#157347',
              },
            }}
          >
            Exportar Excel
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <FilterContainer component="form" onSubmit={handleFilterSubmit}>
        <Grid container spacing={3} alignItems="center">
          {/* Date Type Selector */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="date-type-label">Tipo de Filtro de Fecha</InputLabel>
              <Select
                labelId="date-type-label"
                id="date-type"
                value={dateType}
                label="Tipo de Filtro de Fecha"
                onChange={(e) => {
                  setDateType(e.target.value);
                  // Clear inactive values
                  if (e.target.value !== 'range') { setStartDate(''); setEndDate(''); }
                  if (e.target.value !== 'month') setMonth('');
                  if (e.target.value !== 'day') setDay('');
                }}
              >
                <MenuItem value="range">Rango de Fechas</MenuItem>
                <MenuItem value="month">Mes Específico</MenuItem>
                <MenuItem value="day">Día Específico</MenuItem>
                <MenuItem value="all">Todo el Tiempo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Conditional Date Pickers */}
          {dateType === 'range' && (
            <>
              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  label="Desde"
                  type="date"
                  fullWidth
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  label="Hasta"
                  type="date"
                  fullWidth
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          {dateType === 'month' && (
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Seleccione Mes"
                type="month"
                fullWidth
                size="small"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          {dateType === 'day' && (
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Seleccione Día"
                type="date"
                fullWidth
                size="small"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          {dateType === 'all' && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ py: 1, px: 2, bgcolor: '#f8f9fa', borderRadius: '8px', border: '1px dashed #ced4da', display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6c757d' }} />
                <Typography variant="body2" color="text.secondary">Visualizando historial histórico completo.</Typography>
              </Box>
            </Grid>
          )}

          {/* Filter Inputs */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="automation-select-label">Automatización</InputLabel>
              <Select
                labelId="automation-select-label"
                id="automation-select"
                value={automationId}
                label="Automatización"
                onChange={(e) => setAutomationId(e.target.value)}
                disabled={automationsLoading}
              >
                <MenuItem value="">
                  <em>Todas las Automatizaciones</em>
                </MenuItem>
                {automations.map((aut) => {
                  const filterDesc = aut.filtros && aut.filtros.length > 0 
                    ? aut.filtros.map(f => `${f.tipo}: ${f.valor}`).slice(0, 2).join(', ')
                    : 'Sin Filtros';
                  return (
                    <MenuItem key={aut._id} value={aut._id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {aut.plantilla || 'Sin plantilla'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {aut._id} ({filterDesc})
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Ejecutivo"
              fullWidth
              size="small"
              placeholder="Ej: Juan Pérez"
              value={ejecutivo}
              onChange={(e) => setEjecutivo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Plantilla (Template)"
              fullWidth
              size="small"
              placeholder="Ej: saludo_inicial"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', ml: 'auto' }}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              disabled={loading}
              sx={{
                borderColor: '#e9edef',
                color: '#495057',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 3,
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  borderColor: '#ced4da',
                },
              }}
            >
              Limpiar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faFilter} />}
              sx={{
                backgroundColor: '#008069',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 4,
                '&:hover': {
                  backgroundColor: '#006d5a',
                },
              }}
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </FilterContainer>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading state indicator */}
      {loading && !reportData && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#008069', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">Cargando reporte de automatizaciones...</Typography>
        </Box>
      )}

      {/* Dashboard View */}
      {reportData && (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Attempted */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <MetricCard gradient="linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)">
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Ejecuciones
                    </Typography>
                    <Box sx={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faRobot} size="lg" />
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {summary.totalExecutions ?? 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '12px' }}>
                    Intentos de automatización
                  </Typography>
                </CardContent>
              </MetricCard>
            </Grid>

            {/* Sent */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <MetricCard gradient="linear-gradient(135deg, #064e3b 0%, #10b981 100%)">
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Enviados
                    </Typography>
                    <Box sx={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faPaperPlane} size="lg" />
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {summary.totalSent ?? 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '12px' }}>
                    Plantillas enviadas con éxito
                  </Typography>
                </CardContent>
              </MetricCard>
            </Grid>

            {/* Replied */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <MetricCard gradient="linear-gradient(135deg, #4c1d95 0%, #6366f1 100%)">
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Respondidos
                    </Typography>
                    <Box sx={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faReply} size="lg" />
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {summary.totalReplied ?? 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '12px' }}>
                    Tasa de Resp: {((summary.replyRate ?? 0)).toFixed(1)}%
                  </Typography>
                </CardContent>
              </MetricCard>
            </Grid>

            {/* Skipped */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <MetricCard gradient="linear-gradient(135deg, #78350f 0%, #f59e0b 100%)">
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Omitidos
                    </Typography>
                    <Box sx={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faTimesCircle} size="lg" />
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {summary.totalSkipped ?? 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '12px' }}>
                    Filtros/Condiciones no cumplidos
                  </Typography>
                </CardContent>
              </MetricCard>
            </Grid>

            {/* Failed */}
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <MetricCard gradient="linear-gradient(135deg, #7f1d1d 0%, #f43f5e 100%)">
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Fallidos
                    </Typography>
                    <Box sx={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {summary.totalFailed ?? 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '12px' }}>
                    Error de envío: {((summary.failureRate ?? 0)).toFixed(1)}%
                  </Typography>
                </CardContent>
              </MetricCard>
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Chart Status */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: '16px', border: '1px solid #e9edef', boxShadow: 'none' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#111b21' }}>
                    Distribución de Ejecuciones
                  </Typography>
                  <Box sx={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {summary.totalAttempted > 0 ? (
                      <div id="status-breakdown-chart" style={{ width: '100%' }}></div>
                    ) : (
                      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <FontAwesomeIcon icon={faInfoCircle} size="2x" style={{ marginBottom: '8px', color: '#adb5bd' }} />
                        <Typography variant="body2">No hay datos suficientes para graficar</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Chart Executives */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: '16px', border: '1px solid #e9edef', boxShadow: 'none' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#111b21' }}>
                    Rendimiento por Ejecutivo (Top 8)
                  </Typography>
                  <Box sx={{ height: '320px' }}>
                    {reportData.ejecutivosSummary && reportData.ejecutivosSummary.length > 0 ? (
                      <div id="executives-perf-chart" style={{ width: '100%' }}></div>
                    ) : (
                      <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <FontAwesomeIcon icon={faInfoCircle} size="2x" style={{ marginBottom: '8px', color: '#adb5bd' }} />
                          <Typography variant="body2">No se encontraron resúmenes de ejecutivos</Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Table Executives Summary */}
          {reportData.ejecutivosSummary && reportData.ejecutivosSummary.length > 0 && (
            <Card sx={{ borderRadius: '16px', border: '1px solid #e9edef', boxShadow: 'none', mb: 4 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e9edef' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21' }}>
                    Desglose por Ejecutivo
                  </Typography>
                </Box>
                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Ejecutivo</StyledTableCell>
                        <StyledTableCell align="right">Intentos</StyledTableCell>
                        <StyledTableCell align="right">Enviados</StyledTableCell>
                        <StyledTableCell align="right">Respondidos</StyledTableCell>
                        <StyledTableCell align="right">Omitidos</StyledTableCell>
                        <StyledTableCell align="right">Fallidos</StyledTableCell>
                        <StyledTableCell align="right">Tasa Error</StyledTableCell>
                        <StyledTableCell align="right">Tasa Resp.</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.ejecutivosSummary.map((row, idx) => (
                        <TableRow key={idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 500, color: '#111b21' }}>
                            {row.ejecutivo || 'Sistema/General'}
                          </TableCell>
                          <TableCell align="right">{row.totalAttempted ?? row.attemptedCount ?? 0}</TableCell>
                          <TableCell align="right">{row.totalSent ?? row.sentCount ?? 0}</TableCell>
                          <TableCell align="right">{row.totalReplied ?? row.repliedCount ?? 0}</TableCell>
                          <TableCell align="right">{row.totalSkipped ?? row.skippedCount ?? 0}</TableCell>
                          <TableCell align="right" sx={{ color: (row.totalFailed ?? row.failedCount ?? 0) > 0 ? '#f43f5e' : 'inherit' }}>
                            {row.totalFailed ?? row.failedCount ?? 0}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: (row.failureRate ?? 0) > 10 ? '#f43f5e' : '#137333' }}>
                            {((row.failureRate ?? 0)).toFixed(1)}%
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1a73e8' }}>
                            {((row.replyRate ?? 0)).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Tabs Selection */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, val) => setActiveTab(val)}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#008069',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#495057',
                  '&.Mui-selected': {
                    color: '#008069',
                  },
                }
              }}
            >
              <Tab value="executions" label="Historial de Ejecuciones" />
              <Tab value="audits" label="Auditoría de Envíos (Mensajes)" />
            </Tabs>
          </Box>

          {activeTab === 'executions' && (
            <>
              {/* Main Logs Table */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#111b21' }}>
                Historial de Ejecuciones ({pagination.total ?? 0})
              </Typography>
              <StyledTableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Fecha / Hora</StyledTableCell>
                      <StyledTableCell>Celular / Cliente</StyledTableCell>
                      <StyledTableCell>Plantilla (Template)</StyledTableCell>
                      <StyledTableCell>Ejecutivo</StyledTableCell>
                      <StyledTableCell>ID Auto.</StyledTableCell>
                      <StyledTableCell>Estado</StyledTableCell>
                      <StyledTableCell align="center">Acciones</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <CircularProgress sx={{ color: '#008069', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">Cargando registros...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : !reportData.items || reportData.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                          <FontAwesomeIcon icon={faInfoCircle} size="2x" style={{ marginBottom: '8px', color: '#adb5bd' }} />
                          <Typography variant="body1">No se encontraron registros de ejecución</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportData.items.map((item, idx) => {
                        const chipStyle = getStatusChipColor(item.status || item.eventType);
                        return (
                          <TableRow key={item._id || idx} hover>
                            <TableCell sx={{ fontSize: '13px', color: '#495057' }}>
                              {item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500, color: '#111b21' }}>
                              {item.celular || item.recipient || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', color: '#495057' }}>
                              {item.templateName || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', color: '#495057' }}>
                              {item.ejecutivo || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '12px', fontFamily: 'monospace', color: '#6c757d' }}>
                              {item.automationId || '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={chipStyle.label}
                                size="small"
                                sx={{
                                  backgroundColor: chipStyle.bg,
                                  color: chipStyle.text,
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  borderRadius: '6px'
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Ver detalle del registro" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => openDetails(item)}
                                  sx={{
                                    color: '#008069',
                                    '&:hover': {
                                      backgroundColor: '#e6f4ea'
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {/* Pagination Controls */}
                {pagination && pagination.totalPages > 0 && (
                  <Box sx={{ px: 3, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e9edef', backgroundColor: '#ffffff' }}>
                    <Typography variant="body2" color="text.secondary">
                      Página <b>{pagination.page}</b> de <b>{pagination.totalPages}</b> (Total: {pagination.total} registros)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 80, mr: 2 }}>
                        <Select
                          id="limit-select"
                          value={limit}
                          onChange={(e) => {
                            setLimit(e.target.value);
                            setPage(1);
                          }}
                          sx={{ fontSize: '12px', height: '32px' }}
                        >
                          <MenuItem value={10}>10 / pág</MenuItem>
                          <MenuItem value={20}>20 / pág</MenuItem>
                          <MenuItem value={50}>50 / pág</MenuItem>
                          <MenuItem value={100}>100 / pág</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!pagination.hasPrevPage || loading}
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        startIcon={<FontAwesomeIcon icon={faChevronLeft} size="xs" />}
                        sx={{
                          borderColor: '#e9edef',
                          color: '#495057',
                          textTransform: 'none',
                          height: '32px',
                          borderRadius: '8px',
                          '&:hover': { backgroundColor: '#f8f9fa' }
                        }}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!pagination.hasNextPage || loading}
                        onClick={() => setPage(prev => prev + 1)}
                        endIcon={<FontAwesomeIcon icon={faChevronRight} size="xs" />}
                        sx={{
                          borderColor: '#e9edef',
                          color: '#495057',
                          textTransform: 'none',
                          height: '32px',
                          borderRadius: '8px',
                          '&:hover': { backgroundColor: '#f8f9fa' }
                        }}
                      >
                        Siguiente
                      </Button>
                    </Box>
                  </Box>
                )}
              </StyledTableContainer>
            </>
          )}

          {activeTab === 'audits' && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#111b21' }}>
                Detalle de Mensajes Enviados ({auditData?.total ?? 0})
              </Typography>
              {auditError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                  {auditError}
                </Alert>
              )}
              <StyledTableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Fecha / Hora</StyledTableCell>
                      <StyledTableCell>Evento</StyledTableCell>
                      <StyledTableCell>Destinatario (Celular)</StyledTableCell>
                      <StyledTableCell>ID de Mensaje (wamid)</StyledTableCell>
                      <StyledTableCell>Estado en WhatsApp</StyledTableCell>
                      <StyledTableCell>Detalles / Métricas</StyledTableCell>
                      <StyledTableCell align="center">Acciones</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <CircularProgress sx={{ color: '#008069', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">Cargando registros de auditoría...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : !auditData || !auditData.items || auditData.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                          <FontAwesomeIcon icon={faInfoCircle} size="2x" style={{ marginBottom: '8px', color: '#adb5bd' }} />
                          <Typography variant="body1">No se encontraron registros de auditoría</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      auditData.items.map((item, idx) => {
                        const waid = item.messageId;
                        const hasWaid = waid && waid !== '0';
                        const verifyState = hasWaid ? verifyingWaid[waid] : null;
                        const errorMsg = hasWaid ? waidErrors[waid] : null;
                        const verifiedStatus = hasWaid ? verifiedStatuses[waid] : null;

                        const getEventChipColor = (type) => {
                          const t = String(type || '').toLowerCase();
                          if (t === 'message_sent' || t === 'success') {
                            return { bg: '#e6f4ea', text: '#137333', label: 'Mensaje Enviado' };
                          }
                          if (t === 'run_end') {
                            return { bg: '#f3e8ff', text: '#6b21a8', label: 'Fin de Ejecución' };
                          }
                          if (t === 'run_start') {
                            return { bg: '#e0f2fe', text: '#0369a1', label: 'Inicio Ejecución' };
                          }
                          if (t === 'error' || t.includes('fail')) {
                            return { bg: '#fce8e6', text: '#c5221f', label: 'Error' };
                          }
                          return { bg: '#f1f3f4', text: '#3c4043', label: type };
                        };

                        const eventChip = getEventChipColor(item.eventType);
                        const phone = item.originalDestination || item.effectiveDestination;
                        const basePath = user?.roles?.includes("ROLE_ADMIN") ? "/admin/chat/" : "/dashboard/chat/";

                        return (
                          <TableRow key={item._id || idx} hover>
                            <TableCell sx={{ fontSize: '13px', color: '#495057' }}>
                              {item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={eventChip.label}
                                size="small"
                                sx={{
                                  backgroundColor: eventChip.bg,
                                  color: eventChip.text,
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  borderRadius: '6px'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {phone ? (
                                <Tooltip title="Abrir chat en nueva pestaña" arrow>
                                  <a 
                                    href={`${basePath}${phone}`} 
                                    target="_blank" 
                                    rel="noreferrer"
                                    style={{
                                      color: '#008069',
                                      fontWeight: 600,
                                      textDecoration: 'none',
                                      borderBottom: '1px dashed #008069'
                                    }}
                                  >
                                    {phone}
                                  </a>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#6c757d', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={waid || ''}>
                              {waid || '-'}
                            </TableCell>
                            <TableCell>
                              {hasWaid ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={verifyState === 'checking'}
                                    onClick={() => handleVerifyMessageStatus(waid)}
                                    sx={{
                                      fontSize: '10px',
                                      py: '2px',
                                      px: '8px',
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      borderColor: verifyState === 'verified' ? '#81c784' : verifyState === 'error' ? '#e57373' : '#90caf9',
                                      color: verifyState === 'verified' ? '#2e7d32' : verifyState === 'error' ? '#c62828' : '#1565c0',
                                      backgroundColor: verifyState === 'verified' ? '#e8f5e9' : verifyState === 'error' ? '#ffebee' : '#e3f2fd',
                                      '&:hover': {
                                        backgroundColor: verifyState === 'verified' ? '#c8e6c9' : verifyState === 'error' ? '#ffcdd2' : '#bbdefb',
                                        borderColor: verifyState === 'verified' ? '#4caf50' : verifyState === 'error' ? '#f44336' : '#2196f3',
                                      }
                                    }}
                                  >
                                    {verifyState === 'checking' ? 'Verificando...' : verifyState === 'verified' ? `✓ ${verifiedStatus || 'OK'}` : verifyState === 'error' ? '⚠ Error' : 'Verificar'}
                                  </Button>
                                  {verifyState === 'error' && errorMsg && (
                                    <Tooltip title={errorMsg} arrow>
                                      <IconButton size="small" sx={{ color: '#d32f2f' }}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} size="xs" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '12px', color: '#495057' }}>
                              {item.eventType === 'run_end' ? (
                                <span>
                                  Enviados: <b>{item.sentCount}</b> / Procesados: <b>{item.processedCount}</b> (Err: {item.errorCount}) 
                                  {item.durationMs && ` | Duración: ${(item.durationMs/1000).toFixed(1)}s`}
                                </span>
                              ) : item.policyId ? (
                                <span>Póliza ID: <span style={{ fontFamily: 'monospace' }}>{item.policyId}</span></span>
                              ) : '-'}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Ver JSON completo" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => openDetails(item)}
                                  sx={{
                                    color: '#008069',
                                    '&:hover': {
                                      backgroundColor: '#e6f4ea'
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {/* Audits Pagination */}
                {auditData && auditData.totalPages > 0 && (
                  <Box sx={{ px: 3, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e9edef', backgroundColor: '#ffffff' }}>
                    <Typography variant="body2" color="text.secondary">
                      Página <b>{auditData.page}</b> de <b>{auditData.totalPages}</b> (Total: {auditData.total} registros)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 80, mr: 2 }}>
                        <Select
                          id="audit-limit-select"
                          value={auditLimit}
                          onChange={(e) => {
                            setAuditLimit(e.target.value);
                            setAuditPage(1);
                          }}
                          sx={{ fontSize: '12px', height: '32px' }}
                        >
                          <MenuItem value={10}>10 / pág</MenuItem>
                          <MenuItem value={20}>20 / pág</MenuItem>
                          <MenuItem value={50}>50 / pág</MenuItem>
                          <MenuItem value={100}>100 / pág</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!auditData.hasPrevPage || auditLoading}
                        onClick={() => setAuditPage(prev => Math.max(1, prev - 1))}
                        startIcon={<FontAwesomeIcon icon={faChevronLeft} size="xs" />}
                        sx={{
                          borderColor: '#e9edef',
                          color: '#495057',
                          textTransform: 'none',
                          height: '32px',
                          borderRadius: '8px',
                          '&:hover': { backgroundColor: '#f8f9fa' }
                        }}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!auditData.hasNextPage || auditLoading}
                        onClick={() => setAuditPage(prev => prev + 1)}
                        endIcon={<FontAwesomeIcon icon={faChevronRight} size="xs" />}
                        sx={{
                          borderColor: '#e9edef',
                          color: '#495057',
                          textTransform: 'none',
                          height: '32px',
                          borderRadius: '8px',
                          '&:hover': { backgroundColor: '#f8f9fa' }
                        }}
                      >
                        Siguiente
                      </Button>
                    </Box>
                  </Box>
                )}
              </StyledTableContainer>
            </Box>
          )}
        </Box>
      )}

      {/* Empty State when no query has run */}
      {!reportData && !loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, bgcolor: '#ffffff', borderRadius: '16px', border: '1px solid #e9edef' }}>
          <Box sx={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#e6f4ea', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <FontAwesomeIcon icon={faRobot} size="2x" style={{ color: '#008069' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 1 }}>
            Visualice el Reporte de Automatizaciones
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '400px', textAlign: 'center', mb: 3 }}>
            Seleccione el tipo de filtro de fecha y presione "Filtrar" para cargar las estadísticas de ejecución de sus flujos automatizados.
          </Typography>
          <Button
            variant="contained"
            onClick={() => loadReport(1)}
            startIcon={<FontAwesomeIcon icon={faFilter} />}
            sx={{
              backgroundColor: '#008069',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 4,
              '&:hover': {
                backgroundColor: '#006d5a',
              },
            }}
          >
            Cargar Datos Iniciales
          </Button>
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: '1px solid #e9edef' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FontAwesomeIcon icon={faFileInvoice} style={{ color: '#008069' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21' }}>
              Detalles de Ejecución de Automatización
            </Typography>
          </Box>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {selectedItem && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      Fecha y Hora de Ejecución
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#111b21', fontWeight: 500 }}>
                      {selectedItem.createdAt ? new Date(selectedItem.createdAt).toLocaleString() : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      Celular / Recipiente
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#111b21', fontWeight: 500 }}>
                      {selectedItem.celular || selectedItem.recipient || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      ID de Automatización
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f1f3f4', px: 1, py: 0.5, borderRadius: '4px', display: 'inline-block' }}>
                      {selectedItem.automationId || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      Nombre de la Plantilla
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#111b21', fontWeight: 500 }}>
                      {selectedItem.templateName || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      Ejecutivo Responsable
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#111b21', fontWeight: 500 }}>
                      {selectedItem.ejecutivo || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      Estado
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {(() => {
                        const chipStyle = getStatusChipColor(selectedItem.status || selectedItem.eventType);
                        return (
                          <Chip
                            label={chipStyle.label}
                            size="small"
                            sx={{
                              backgroundColor: chipStyle.bg,
                              color: chipStyle.text,
                              fontWeight: 600,
                            }}
                          />
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Grid>
              {selectedItem.errorMessage && (
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                      Mensaje de Error
                    </Typography>
                    <Alert severity="error" icon={<FontAwesomeIcon icon={faExclamationTriangle} />} sx={{ borderRadius: '8px' }}>
                      {selectedItem.errorMessage}
                    </Alert>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Objeto de Auditoría (JSON Completo)
                </Typography>
                <DetailPre>
                  {JSON.stringify(selectedItem, null, 2)}
                </DetailPre>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #e9edef' }}>
          <Button
            onClick={() => setDetailOpen(false)}
            sx={{
              color: '#495057',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#f1f3f5' }
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
